import React, { useState } from 'react';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VerifiedIcon from '@mui/icons-material/Verified';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RuleIcon from '@mui/icons-material/Rule';
import EditIcon from '@mui/icons-material/Edit';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CloseIcon from '@mui/icons-material/Close';
import LaunchIcon from '@mui/icons-material/Launch';

export function ForArtists() {
  const [showModal, setShowModal] = useState(false);
  
  const handleApplyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setShowModal(true);
  };
  
  const handleConfirm = () => {
    setShowModal(false);
    window.location.href = 'https://forms.gle/9TnenHfhLQQ4MAWq5';
  };
  
  const handleCancel = () => {
    setShowModal(false);
  };
  return (
    <>
      {/* Custom Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-deep-night/80">
          <div className="bg-deep-night border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-outfit font-semibold text-pearl-white">Leaving Naia</h3>
              <button 
                onClick={handleCancel}
                className="text-slate-grey hover:text-pearl-white transition-colors"
              >
                <CloseIcon />
              </button>
            </div>
            <div className="mb-6">
              <div className="bg-naia-pink/10 p-4 rounded-lg mb-4">
                <p className="text-slate-grey">
                  You are about to leave the Naia website and go to an external application portal.
                </p>
              </div>
              <p className="text-slate-grey">
                Do you want to continue?
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-pearl-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="px-4 py-2 rounded-lg bg-naia-pink hover:bg-naia-pink/90 text-pearl-white transition-colors flex items-center gap-2"
              >
                Continue <LaunchIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-6 py-12">
      <h1 className="font-outfit text-3xl font-semibold mb-8 flex items-center gap-3 text-pearl-white relative after:absolute after:bottom-0 after:left-0 after:w-24 after:h-px after:bg-gradient-to-r after:from-naia-pink after:to-transparent">
        <span>For Artists</span>
      </h1>

      {/* Hero section */}
      <section className="bg-gradient-to-br from-naia-pink/20 to-deep-night rounded-xl p-8 mb-8">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="flex-1">
            <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">Join Us</h2>
            <p className="text-lg text-slate-grey mb-6">
              Naia is <span className="text-pearl-white font-medium">the</span> platform for VSynth producers to showcase their art to a dedicated audience of enthusiasts.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="/artists/panel" 
                className="bg-naia-pink hover:bg-naia-pink/90 text-pearl-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                Artist Portal
              </a>
              <a 
                href="/apply" 
                onClick={handleApplyClick}
                className="bg-white/10 hover:bg-white/15 text-pearl-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              >
                Apply Now
              </a>
            </div>
          </div>
          <div className="flex-shrink-0">
            <div className="w-64 h-64 rounded-full bg-naia-pink/20 flex items-center justify-center">
              <MusicNoteIcon style={{ fontSize: 80 }} className="text-naia-pink" />
            </div>
          </div>
        </div>
      </section>

      {/* Why Naia section */}
      <section className="bg-white/5 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-outfit font-semibold mb-6 text-pearl-white">Why Naia is <span className="text-naia-pink">The Place</span> for VSynth Producers</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center">
                <VerifiedIcon className="text-naia-pink" />
              </div>
              <h3 className="text-lg font-medium text-pearl-white">Dedicated Platform</h3>
            </div>
            <p className="text-slate-grey">
              A streaming service built specifically for VSynth music, connecting you with listeners who appreciate your craft.
            </p>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center">
                <RuleIcon className="text-naia-pink" />
              </div>
              <h3 className="text-lg font-medium text-pearl-white">Simple Guidelines</h3>
            </div>
            <p className="text-slate-grey">
              Straightforward content guidelines that respect artistic freedom while ensuring a safe platform for all.
            </p>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center">
                <HourglassTopIcon className="text-naia-pink" />
              </div>
              <h3 className="text-lg font-medium text-pearl-white">Future Growth</h3>
            </div>
            <p className="text-slate-grey">
              Join early as we build a community-focused platform for all VSynth fans.
            </p>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center">
                <MusicNoteIcon className="text-naia-pink" />
              </div>
              <h3 className="text-lg font-medium text-pearl-white">Free and Unlimited</h3>
            </div>
            <p className="text-slate-grey">
              Naia doesn't charge you anything to upload your music nor do we limit how much you can upload.
            </p>
          </div>
        </div>
      </section>

      {/* Submission Guidelines */}
      <section className="bg-white/5 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-outfit font-semibold mb-6 text-pearl-white">Submission Guidelines</h2>
        
        <div className="space-y-6">
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                <VerifiedIcon className="text-naia-pink" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-pearl-white mb-2">Review Process</h3>
                <p className="text-slate-grey mb-3">
                  While acceptance is close to guaranteed as long as you follow the Content Guidelines, all artist profiles and song details are manually reviewed before publication to ensure platform safety.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                <AccessTimeIcon className="text-naia-pink" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-pearl-white mb-2">Review Timeline</h3>
                <p className="text-slate-grey mb-3">
                  While we try to review songs in less than 48 hours, always submit songs for review at least one week in advance to guarantee they'll be published on your desired date.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                <CalendarTodayIcon className="text-naia-pink" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-pearl-white mb-2">Publication Scheduling</h3>
                <p className="text-slate-grey mb-3">
                  Unless a specific date is specified in song submissions, songs will be published as soon as they are reviewed. If you specify a date but no time, songs will be published at 00:00 CEST on the specified day.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/5 p-5 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-naia-pink/20 flex items-center justify-center flex-shrink-0 mt-1">
                <EditIcon className="text-naia-pink" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-pearl-white mb-2">Editing Submissions</h3>
                <p className="text-slate-grey mb-3">
                  Song details cannot be changed during the review process. You can only edit song details after they have been published.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monetization Notice */}
      <section className="bg-white/5 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-outfit font-semibold mb-4 text-pearl-white">Monetization</h2>
        <div className="bg-naia-pink/10 border border-naia-pink/30 rounded-lg p-4">
          <p className="text-slate-grey">
            <span className="text-pearl-white font-medium">Note:</span> Naia does not currently provide monetary compensation for streams. We are actively working on a monetization program, but this is strictly a Work-In-Progress feature and may only be available to a select number of artists. For now, please use the Purchase Link feature to monetize your music.
          </p>
        </div>
      </section>

      {/* FAQ section */}
      <section className="bg-white/5 rounded-xl p-6">
        <h2 className="text-xl font-outfit font-semibold mb-6 text-pearl-white">FAQ</h2>
        
        <div className="space-y-4">
          <div className="border-b border-white/10 pb-4">
            <h3 className="text-lg font-medium text-pearl-white mb-2">How do I apply to become an artist on Naia?</h3>
            <p className="text-slate-grey">
              You can apply through our <a href="/artists/panel" className="text-naia-pink hover:underline">artist portal</a>. The application process is simple and requires basic information about you and your music.
            </p>
          </div>
          
          <div className="border-b border-white/10 pb-4">
            <h3 className="text-lg font-medium text-pearl-white mb-2">What file formats are accepted for song uploads?</h3>
            <p className="text-slate-grey">
              We accept high-quality MP3 (320kbps) and WAV files for all song uploads.
            </p>
          </div>
          
          <div className="border-b border-white/10 pb-4">
            <h3 className="text-lg font-medium text-pearl-white mb-2">Can I upload songs using any VSynth software?</h3>
            <p className="text-slate-grey">
              Yes! We welcome music created with any VSynth software, including but not limited to VOCALOID, CeVIO, SynthV, UTAU, and others.
            </p>
          </div>
          
          <div className="border-b border-white/10 pb-4">
            <h3 className="text-lg font-medium text-pearl-white mb-2">When will monetization be available?</h3>
            <p className="text-slate-grey">
              We're actively developing our monetization program and will notify all artists as soon as it's ready for launch. We're committed to creating a fair system that rewards creators.
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-pearl-white mb-2">How do I access the artist panel?</h3>
            <p className="text-slate-grey">
              If you're already an approved artist, you can access the <a href="/artists/panel" className="text-naia-pink hover:underline">artist panel</a> to manage your profile, upload songs, and view statistics.
            </p>
          </div>
        </div>
      </section>

      {/* Footnote */}
      <footer className="mt-12 pt-6 border-t border-white/10 text-center">
        <p className="text-sm text-slate-grey">VOCALOID is a trademark of Yamaha Corporation. Synthesizer V is a trademark of Dreamtonics Co,. Ltd. CeVIO is a trademark of CeVIO. Naia is not affiliated with any of these companies.</p>
      </footer>
      </div>
    </>
  );
}